import React, { useState, useEffect } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, Container, Input } from "reactstrap";
import LaddaButton, { SLIDE_LEFT } from "react-ladda";
import { Field } from "../../../..";
import { NumberFormatInput } from "../../../NumberFormatInput/NumberFormatInput";
import { FormSelect, LoadingMessage } from "../../Controls";
import { Mode } from "../../types";
import { showToast } from "../../../../utils/Helper";

const initialFields = {
  RiskRangeId: "",
  RiskRange: "",
  RiskRangePercentage: "",
  RiskRangePercentageIssuer: "",
  RiskRangeScore: "",
  RiskRangeColor: "#000",
};

const initialExtraFields = (defaultValues) => {
  initialFields.RiskRangeScore = defaultValues.RiskRangeScore;
  return initialFields;
};

const NewCreditRangeModal = ({
  open,
  toggleModal,
  modal,
  agencies,
  getRatingsByRiskRangeId,
  removeRatingFromRange,
  services,
  lastOrder,
}) => {
  const [fields, setFields] = useState(initialExtraFields({ RiskRangeScore: lastOrder || 0 }));
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [ratings, setRatings] = useState([]);
  const [fetchingRatings, setFetchingRatings] = useState(false);

  useEffect(() => {
    if (modal.action == Mode.Update) {
      getRatingsByRiskRangeId(modal.update.RiskRangeId, () =>
        setFetchingRatings((prevState) => !prevState)
      ).then((creditRatings) => {
        setRatings(creditRatings);
      });
    }
  }, []);

  useEffect(() => {
    const { update } = modal;
    if (update != null) {
      setFields({
        RiskRangeId: update.RiskRangeId,
        RiskRange: update.RiskRange,
        RiskRangePercentage: update.RiskRangePercentage,
        RiskRangePercentageIssuer: update.RiskRangePercentageIssuer,
        RiskRangeScore: update.RiskRangeScore,
        RiskRangeColor: update.RiskRangeColor,
        Ratings: update.Ratings,
      });
      setRatings(update.Ratings || []);
    }
  }, [modal.update]);

  const handleSave = async () => {
    const errors = validate(fields, ratings);
    setErrors(errors);

    if (Object.keys(errors).length == 0) {
      setLoading(true);
      const result = await modal.onConfirm({ ...fields }, getNewRatings(ratings));
      setLoading(false);
      if (result == 200) {
        toggleModal();
        setFields(initialFields);
      }
    }
  };

  const onChange = (name, value) => {
    setFields({ ...fields, [name]: value });
    setErrors({ ...errors, [name]: undefined });
  };

  return (
    <Modal isOpen={open} toggle={toggleModal}>
      <ModalHeader toggle={toggleModal}>Add Credit Range</ModalHeader>
      <ModalBody>
        <Container>
          <Row>
            <Col lg="12">
              <Field label="Name" error={errors.RiskRange}>
                <Input
                  type="text"
                  placeholder="Enter Range Name"
                  onChange={(e) => onChange("RiskRange", e.target.value)}
                  value={fields.RiskRange}
                  name="RiskRange"
                />
              </Field>
              <Field label="Limit Percentage" error={errors.RiskRangePercentage}>
                <NumberFormatInput
                  type="text"
                  customInput={Input}
                  placeholder={"Enter Limit"}
                  name="RiskRangePercentage"
                  value={fields.RiskRangePercentage}
                  onValueChange={(e) =>
                    onChange("RiskRangePercentage", isNaN(e.floatValue) ? e.value : e.floatValue)
                  }
                  decimalScale={2}
                  dataTypeDecimalName="price"
                />
              </Field>
              <Field label="Limit Issuer" error={errors.RiskRangePercentageIssuer}>
                <NumberFormatInput
                  type="text"
                  customInput={Input}
                  placeholder={"Enter Limit Issuer"}
                  name="RiskRangePercentageIssuer"
                  value={fields.RiskRangePercentageIssuer}
                  onValueChange={(e) =>
                    onChange("RiskRangePercentageIssuer", isNaN(e.floatValue) ? e.value : e.floatValue)
                  }
                  decimalScale={2}
                  dataTypeDecimalName="price"
                />
              </Field>
              <Field label="Ordenation" error={errors.RiskRangeScore}>
                <NumberFormatInput
                  type="text"
                  customInput={Input}
                  placeholder={"Enter Ordenation Score"}
                  name="RiskRangeScore"
                  value={fields.RiskRangeScore}
                  onValueChange={(e) =>
                    onChange("RiskRangeScore", isNaN(e.floatValue) ? e.value : e.floatValue)
                  }
                  decimalScale={2}
                  dataTypeDecimalName="price"
                />
              </Field>
              <div className="w-25">
                <Field label="Color" error={errors.RiskRangeColor}>
                  <Input
                    type="color"
                    placeholder="Enter Range Color"
                    onChange={(e) => onChange("RiskRangeColor", e.target.value)}
                    value={fields.RiskRangeColor}
                  />
                </Field>
              </div>
            </Col>
          </Row>
          <AssociateRatings
            ratings={ratings}
            setRatings={setRatings}
            agencies={agencies}
            errors={errors}
            modal={modal}
            fetchingRatings={fetchingRatings}
            services={services}
            removeRatingFromRange={removeRatingFromRange}
          />
        </Container>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggleModal}>
          Cancel
        </Button>
        <LaddaButton
          className="btn btn-ladda btn-primary pl-3 pr-3"
          loading={loading}
          disabled={false}
          onClick={handleSave}
          data-style={SLIDE_LEFT}
        >
          <i className="fa fa-floppy-o mr-1" aria-hidden="true"></i>
          {modal.update != null ? "Update" : "Save"}
        </LaddaButton>
      </ModalFooter>
    </Modal>
  );
};

const AssociateRatings = ({
  ratings,
  setRatings,
  agencies,
  modal,
  errors,
  fetchingRatings,
  services,
  removeRatingFromRange,
}) => {
  const addNewRating = () => {
    setRatings([
      ...ratings,
      {
        id: Date.now(),
        CreditRatingScore: "",
        CreditRatingScoreValue: "",
        CreditRatingAgencyId: "",
        IsNew: true,
      },
    ]);
  };

  const updateRating = (index, field, value) => {
    const updatedRatings = [...ratings];
    updatedRatings[index][field] = value;
    setRatings(updatedRatings);
  };

  const removeRating = (rating, index) => {
    if (!rating.IsNew) {
      removeRatingCallApi(rating);
      return;
    }

    const updatedRatings = ratings.filter((_, i) => i !== index);
    setRatings(updatedRatings);
  };

  const removeRatingCallApi = (rating) => {
    const response = confirm("Are you sure?");
    if (!response) return;

    const request = {
      ratingId: rating.CreditRatingScore,
      rangeId: rating.CreditRatingRangeId,
      agencyId: rating.CreditRatingAgencyId,
    };

    services.deleteCreditRatingFromRange &&
      services.deleteCreditRatingFromRange(request).then(({ response }) => {
        if (response && response.result) {
          const { Code, Description } = response.result;
          Code == 200 ? removeRatingMemory(rating) : showToast(Description, true);
        }
      });
  };

  const removeRatingMemory = (rating) => {
    setRatings((prevState) =>
      prevState.filter(
        (currentRating) =>
          currentRating.CreditRatingAgencyId != rating.CreditRatingAgencyId ||
          currentRating.CreditRatingScore != rating.CreditRatingScore
      )
    );
    removeRatingFromRange(modal.update.RiskRangeId, rating);
    showToast("Removed sucessfully");
  };

  return (
    <div className="mt-4">
      <Row className="justify-content-between align-items-center mb-3">
        <h6 className="ml-4 font-weight-bold">Associated Ratings</h6>
        <Button color="primary" onClick={addNewRating} size="sm">
          <i className="fa fa-plus mr-1" aria-hidden="true"></i> Add New
        </Button>
      </Row>
      <div className="mt-3">
        {ratings.length === 0 && !fetchingRatings ? (
          <p className="text-center text-muted">No Ratings Associated</p>
        ) : (
          ratings.map((rating, index) => {
            const disabledInput = modal.action == Mode.Update && !rating.IsNew;

            return (
              <Row
                key={rating.id}
                className="align-items-center justify-content-center mb-1"
                style={{
                  backgroundColor: "#f9f9f9",
                }}
              >
                <Col lg={3} md={6} sm={12} className="p-1 mt-3">
                  <FormSelect
                    name="CreditRatingAgencyId"
                    label=""
                    selectItems={agencies}
                    getSelectItemId={(i) => i.CreditRatingAgencyId}
                    getSelectItemName={(i) => `${i.CreditRatingAgencyName}`}
                    value={rating.CreditRatingAgencyId}
                    onChange={(name, value) => updateRating(index, name, value)}
                    error={errors[`ratings[${index}].CreditRatingAgencyId`]}
                    disabled={disabledInput}
                  />
                </Col>
                <Col lg={4} md={6} sm={12} className="p-1">
                  <Field error={errors[`ratings[${index}].CreditRatingScore`]}>
                    <Input
                      type="text"
                      placeholder="Rating Name"
                      name="CreditRatingScore"
                      value={rating.CreditRatingScore}
                      onChange={(e) => updateRating(index, "CreditRatingScore", e.target.value)}
                      style={{
                        height: "34px",
                      }}
                      disabled={disabledInput}
                    />
                  </Field>
                </Col>
                <Col lg={3} md={6} sm={12} className="p-1">
                  <Field error={errors[`ratings[${index}].CreditRatingScoreValue`]}>
                    <NumberFormatInput
                      type="text"
                      customInput={Input}
                      name="CreditRatingScoreValue"
                      placeholder="Score"
                      value={rating.CreditRatingScoreValue}
                      onValueChange={(e) =>
                        updateRating(
                          index,
                          "CreditRatingScoreValue",
                          isNaN(e.floatValue) ? e.value : e.floatValue
                        )
                      }
                      decimalScale={2}
                      dataTypeDecimalName="score"
                      style={{ height: "34px" }}
                      disabled={disabledInput}
                    />
                  </Field>
                </Col>
                <Col lg={1} md={6} sm={12} className="p-1 text-right">
                  <Button color="danger" onClick={() => removeRating(rating, index)} size="sm">
                    <i className="fa fa-trash" aria-hidden="true"></i>
                  </Button>
                </Col>
              </Row>
            );
          })
        )}
        {fetchingRatings && modal.action == Mode.Update && <LoadingMessage text={"Reciving Ratings..."} />}
      </div>
    </div>
  );
};

function validate(fields, ratings) {
  const errors = {};

  Object.entries(fields).forEach(([name, value]) => {
    if (name != "RiskRangeId" && name != "RiskRangeColor" && (value === undefined || value === "" || !value))
      errors[name] = "Required field";
  });

  ratings.forEach((rating, index) => {
    Object.entries(rating).forEach(([name, value]) => {
      if (value === undefined || value === "" || !value) {
        errors[`ratings[${index}].${name}`] = "Required field";
      }
    });
  });

  return errors;
}

function getNewRatings(ratings) {
  return ratings.filter((rating) => rating.IsNew);
}

export default NewCreditRangeModal;
